/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { HeadersFunction, MetaFunction } from "react-router";
import { isRouteErrorResponse, useLoaderData, useRouteError } from "react-router";
import ArticleView from "~/components/blog/ArticalView";
import NodataFound from "~/components/shared-components/NoDataFound";
import { ArticleType, RelatedArticleType } from "~/interface/article/article";
import { getPageMeta } from "~/lib/helperFuncation/metaHelper";
import { MstPersonType } from "~/type";
import { CACHED_PAGE, FALLBACK_IMAGE_URL, LANG } from "~/utils/constant";
import { computeEtagFromData } from "~/utils/helper-funcations";
import { metaService } from "~/utils/metaService";
import { ApiService } from "~/utils/service";

export interface LoaderData {
  articleData: ArticleType | null;
  title: string;
  authorName: string | null;
  relatedArticle: RelatedArticleType[];
  blogAuthor: MstPersonType[];
  tagsData: MstPersonType[];
  placeName: string | null;
  metaTitle?: string;
  metaDescription?: string;
  ogImage?: string;
}

export const meta: MetaFunction = ({ location, data, params }) => {
  const pathname = location?.pathname || "/";
  const lang = params.lang || "en";

  const articleData = data as LoaderData;
  const meta = getPageMeta("blog", lang);

  return metaService({
    title: articleData?.metaTitle || meta.metaTitle,
    description: articleData?.metaDescription || meta.metaDescription,
    ogTitle: articleData?.metaTitle || meta.ogTitle,
    ogDescription: articleData?.metaDescription || meta.ogDescription,
    ogUrl: pathname,
    ogImg: articleData?.ogImage || meta.ogImage || FALLBACK_IMAGE_URL,
    ogWidth: meta.ogImageWidth || "1200",
    ogHeight: meta.ogImageHeight || "630",
    canonicalLink: pathname,
    hreflangs: [
      { hrefLang: "x-default", href: `${pathname}` },
      { hrefLang: "en", href: `${pathname}` },
      { hrefLang: "gu", href: `${pathname}` },
    ],
  });
};

export const loader = async ({ params, request }: { params: { lang: string; slug: string }; request: Request }): Promise<any> => {
  const { lang, slug } = params;

  const apiService = new ApiService(lang || LANG.english);
  const response: ArticleType = await apiService.getArticleDataBaseOnId("blog", slug);

  const mstResponse = await apiService.getMasterData();
  const relatedArticle = await apiService.getRelatedArticle(slug);

  if (!response) {
    throw new Response(`blog detail not found`, {
      status: 404,
      statusText: `blog detail not found`,
    });
  }

  const blogAuthor: MstPersonType[] = mstResponse?.blog_author;
  const bloCategory: MstPersonType[] = mstResponse?.blog_category;
  const placeData: MstPersonType[] = mstResponse?.place;
  const authorName = blogAuthor?.find((item) => item.id === response?.author)?.text || null;
  const placeName = placeData?.find((item) => item.id === response?.place_id)?.text || null;
  const tags = bloCategory || [];

  const DATA = {
    articleData: response || null,
    title: response?.title || slug,
    authorName: authorName,
    relatedArticle: relatedArticle,
    blogAuthor: blogAuthor,
    tagsData: tags || [],
    placeName: placeName,
    metaTitle: response?.meta_title || response?.title,
    metaDescription: response?.meta_desc || response?.title,
    ogImage: response?.img?.hd?.url || FALLBACK_IMAGE_URL,
  };

  const etag = computeEtagFromData(DATA);
  const ifNoneMatch = request.headers.get("If-None-Match");

  if (ifNoneMatch === etag) {
    return new Response(null, {
      status: 304,
      headers: {
        ETag: etag,
      },
    });
  }

  return Response.json(DATA, {
    headers: { ...CACHED_PAGE.BLOG.HEADERS, ETag: etag },
  });
};

export const headers: HeadersFunction = ({ loaderHeaders }) => loaderHeaders;

// export function headers({ loaderHeaders }: { loaderHeaders: Headers }) {
//   return {
//     "Cache-Control": loaderHeaders.get("Cache-Control") ?? cacheHeaders.none,
//     ETag: cacheHeaders.version,
//   };
// }

const BlogDetailPage = () => {
  const { articleData, title, authorName, relatedArticle, blogAuthor, tagsData, placeName, metaTitle, metaDescription, ogImage } =
    useLoaderData<LoaderData>();

  return (
    <ArticleView
      articleData={articleData}
      title={title}
      authorName={authorName}
      relatedArticle={relatedArticle}
      blogAuthor={blogAuthor}
      tagsData={tagsData}
      placeName={placeName}
    />
  );
};

export function ErrorBoundary() {
  const error = useRouteError();
  let errorMessage = "An unexpected error occurred";

  if (isRouteErrorResponse(error)) {
    errorMessage = error.data;
  } else if (error instanceof Error) {
    errorMessage = String(error.message);
  }
  return <NodataFound text={errorMessage || "No Data Found"} />;
}

export default BlogDetailPage;
